import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import { fetchTransactionStats } from '../../../services/Http/Api';
import { SettingsContext } from "../../SettingsContext";
import { useTranslation } from "react-i18next";
import {
    MdBusiness,
    MdAttachMoney,
    MdMonetizationOn,
    MdReceipt,
    MdPeople,
} from "react-icons/md";
import StyledCard from '../../../shared/layout/cards/StyledCard';
import { useCurrency } from '../../../context/CurrencyProvider';

const TransactionsOverview = ({ period }) => {
    const { t } = useTranslation();
    const { siteSettings } = useContext(SettingsContext);
    const { formatResolvedAmount } = useCurrency();
    const [transactions, setTransactions] = useState([]);
    const [totals, setTotals] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(100);
    const [totalRows, setTotalRows] = useState(0);
    const [sortBy, setSortBy] = useState('date');
    const [sortDirection, setSortDirection] = useState('asc');

    useEffect(() => {
        const loadStats = async () => {
            setLoading(true);
            try {
                const data = await fetchTransactionStats({
                    period,
                    page: currentPage,
                    perPage,
                    sortBy,
                    sortDirection
                });

                setTotals(data.totals);
                setTransactions(data.stats.data);
                setTotalRows(data.stats.total);
            } catch (error) {
                console.error('Failed to load stats', error);
            } finally {
                setLoading(false);
            }
        };

        loadStats();
    }, [period, currentPage, perPage, sortBy, sortDirection]);

    const handlePageChange = (page) => setCurrentPage(page);
    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (column, sortDirection) => {
        const fieldMapping = {
            [t('Transactions.TransactionsOverview.columns.date')]: 'date',
            [t('Transactions.TransactionsOverview.columns.totalPrice')]: 'total_price',
            [t('Transactions.TransactionsOverview.columns.totalRevenue')]: 'total_revenue',
            [t('Transactions.TransactionsOverview.columns.averagePrice')]: 'average_price',
            [t('Transactions.TransactionsOverview.columns.averageRevenue')]: 'average_revenue',
            [t('Transactions.TransactionsOverview.columns.transactionCount')]: 'transaction_count',
        };
        setSortBy(fieldMapping[column.name] || 'date');
        setSortDirection(sortDirection);
    };

    const numberFormatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
    });

    const columns = [
        {
            name: t('Transactions.TransactionsOverview.columns.date'),
            selector: row => new Date(row.date).toLocaleDateString(),
            sortable: true,
        },
        {
            name: t('Transactions.TransactionsOverview.columns.totalPrice'),
            selector: row => formatResolvedAmount({
                eurAmount: row.total_price,
                localAmount: row.total_price_local,
                localCurrencyId: row.currency_id ?? totals?.currency_id,
            }),
            sortable: true,
            cell: row => <MonoText>{formatResolvedAmount({
                eurAmount: row.total_price,
                localAmount: row.total_price_local,
                localCurrencyId: row.currency_id ?? totals?.currency_id,
            })}</MonoText>
        },
        {
            name: t('Transactions.TransactionsOverview.columns.totalRevenue'),
            selector: row => formatResolvedAmount({
                eurAmount: row.total_revenue,
                localAmount: row.total_revenue_local,
                localCurrencyId: row.currency_id ?? totals?.currency_id,
            }),
            sortable: true,
            cell: row => <MonoText>{formatResolvedAmount({
                eurAmount: row.total_revenue,
                localAmount: row.total_revenue_local,
                localCurrencyId: row.currency_id ?? totals?.currency_id,
            })}</MonoText>
        },
        {
            name: t('Transactions.TransactionsOverview.columns.averagePrice'),
            selector: row => formatResolvedAmount({
                eurAmount: row.average_price || 0,
                localAmount: row.average_price_local,
                localCurrencyId: row.currency_id ?? totals?.currency_id,
            }),
            sortable: true,
            cell: row => <MonoText>{formatResolvedAmount({
                eurAmount: row.average_price || 0,
                localAmount: row.average_price_local,
                localCurrencyId: row.currency_id ?? totals?.currency_id,
            })}</MonoText>
        },
        {
            name: t('Transactions.TransactionsOverview.columns.averageRevenue'),
            selector: row => formatResolvedAmount({
                eurAmount: row.average_revenue || 0,
                localAmount: row.average_revenue_local,
                localCurrencyId: row.currency_id ?? totals?.currency_id,
            }),
            sortable: true,
            cell: row => <MonoText>{formatResolvedAmount({
                eurAmount: row.average_revenue || 0,
                localAmount: row.average_revenue_local,
                localCurrencyId: row.currency_id ?? totals?.currency_id,
            })}</MonoText>
        },
        {
            name: t('Transactions.TransactionsOverview.columns.transactionCount'),
            selector: row => row.transaction_count,
            sortable: true
        },
    ];

    const customStyles = {
        table: {
            style: {
                backgroundColor: 'transparent',
            },
        },
        headRow: {
            style: {
                borderBottomColor: '#f3f4f6',
                minHeight: '48px',
            },
        },
        rows: {
            style: {
                minHeight: '64px',
                '&:not(:last-child)': {
                    borderBottomColor: '#f3f4f6',
                },
            },
        },
        headCells: {
            style: {
                color: '#64748b',
                fontSize: '0.75rem',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
            },
        },
        cells: {
            style: {
                color: '#1e293b',
                fontSize: '0.875rem',
            },
        },
    };

    return (
        <Container>
            <StatsGrid>
                <StatBox>
                    <IconWrapper color="#007bff">
                        <MdBusiness size={24} />
                    </IconWrapper>
                    <StatInfo>
                        <StatLabel>{t('Transactions.TransactionsOverview.distinctManufacturers')}</StatLabel>
                        <StatValue>{totals ? totals.distinct_manufacturers : 0}</StatValue>
                    </StatInfo>
                </StatBox>
                <StatBox>
                    <IconWrapper color="#28a745">
                        <MdAttachMoney size={24} />
                    </IconWrapper>
                    <StatInfo>
                        <StatLabel>{t('Transactions.TransactionsOverview.totalPrice')}</StatLabel>
                        <StatValue>{totals ? formatResolvedAmount({
                            eurAmount: totals.total_price || 0,
                            localAmount: totals.total_price_local,
                            localCurrencyId: totals.currency_id,
                        }) : '0.00'}</StatValue>
                    </StatInfo>
                </StatBox>
                <StatBox>
                    <IconWrapper color="#17a2b8">
                        <MdMonetizationOn size={24} />
                    </IconWrapper>
                    <StatInfo>
                        <StatLabel>{t('Transactions.TransactionsOverview.totalRevenue')}</StatLabel>
                        <StatValue>{totals ? formatResolvedAmount({
                            eurAmount: totals.total_revenue || 0,
                            localAmount: totals.total_revenue_local,
                            localCurrencyId: totals.currency_id,
                        }) : '0.00'}</StatValue>
                    </StatInfo>
                </StatBox>
                <StatBox>
                    <IconWrapper color="#ffc107">
                        <MdReceipt size={24} />
                    </IconWrapper>
                    <StatInfo>
                        <StatLabel>{t('Transactions.TransactionsOverview.totalTransactions')}</StatLabel>
                        <StatValue>{totals ? totals.total_transactions : 0}</StatValue>
                    </StatInfo>
                </StatBox>
                <StatBox>
                    <IconWrapper color="#dc3545">
                        <MdPeople size={24} />
                    </IconWrapper>
                    <StatInfo>
                        <StatLabel>{t('Transactions.TransactionsOverview.distinctBuyers')}</StatLabel>
                        <StatValue>{totals ? totals.distinct_buyer_count : 0}</StatValue>
                    </StatInfo>
                </StatBox>
            </StatsGrid>

            <StyledCard>
                <DataTable
                    columns={columns}
                    data={transactions}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    onSort={handleSort}
                    sortServer
                    highlightOnHover
                    progressPending={loading}
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    customStyles={customStyles}
                />
            </StyledCard>
        </Container>
    );
};

export default TransactionsOverview;

const Container = styled.div``;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.25rem;
    margin-bottom: 1.5rem;
`;

const StatBox = styled(StyledCard)`
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0;
    padding: 1.25rem;
`;

const IconWrapper = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: ${({ color }) => `${color}15`};
    color: ${({ color }) => color};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const StatInfo = styled.div`
    display: flex;
    flex-direction: column;
`;

const StatLabel = styled.span`
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 500;
`;

const StatValue = styled.span`
    font-size: 1.125rem;
    font-weight: 700;
    color: #1e293b;
`;

const MonoText = styled.span`
    font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
    font-weight: 500;
`;
